import React, { useMemo } from 'react'
import { TileLayer } from 'react-leaflet'

// Global Harmonized Land Cover (GHLC), IFAD CAPEO / OpenGeoHub, CC BY-SA 4.0. The same rasters
// back app.landmetric.org (layers ifad:land_cover_ifad_mcdt_c_30m_s and ifad:land_cover_ifad_mcdt_10m,
// verified pixel-identical). Rendered by OpenGeoHub's TiTiler from the official COGs with the
// publisher's styles (s3.ecodatacube.eu/ifad/land.cover_ifad.mcdt[_10m]_lv{1,2}_v2.0.sld):
// 'Default' = the 10 level-1 classes, 'Detailed' = level 2 (40 classes at 30 m, 44 at 10 m).
// The two products number their classes differently, so each has its own tables. 10 m labels
// reuse the 30 m wording where the class is the same, so they share translations.
export const GHLC_MODES = ['Default', 'Detailed']
export const GHLC_DEFAULT_MODE = 'Default'
export const GHLC_ATTRIBUTION =
  'GHLC &copy; OpenGeoHub / IFAD CAPEO, <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer">CC BY-SA 4.0</a>'

// 30 m (STAC land.cover_ifad.mcdt_go): one map per year, 2020-2024.
export const GHLC_CLASSES = [
  { value: 1, color: '#d73027', label: '1.1 Built-up' },
  { value: 2, color: '#614d1a', label: '2.1 Annual crops - soybean' },
  { value: 3, color: '#f9a41a', label: '2.2 Annual crops - maize' },
  { value: 4, color: '#f9e79f', label: '2.3 Annual crops - rice' },
  { value: 5, color: '#ffd700', label: '2.4 Annual crops - other' },
  { value: 6, color: '#9e7c2d', label: '2.5 Perenial crops - coffee' },
  { value: 7, color: '#6e4b3a', label: '2.6 Perenial crops - cocoa' },
  { value: 8, color: '#a1887f', label: '2.7 Perenial crops - rubber' },
  { value: 9, color: '#d4ac0d', label: '2.8 Perenial crops - palm' },
  { value: 10, color: '#81c784', label: '3.1 Cultivated grassland' },
  { value: 11, color: '#a5d86a', label: '3.2 Natural grassland' },
  { value: 12, color: '#66d16a', label: '4.1 Tree cover broadleaved evergreen open' },
  { value: 13, color: '#1b9e77', label: '4.2 Tree cover broadleaved evergreen closed' },
  { value: 14, color: '#388e3c', label: '4.3 Tree cover broadleaved deciduous closed to open (>15%)' },
  { value: 15, color: '#006e00', label: '4.4 Tree cover broadleaved deciduous closed (>40%)' },
  { value: 16, color: '#4db6ac', label: '4.5 Tree cover needleleaved evergreen closed to open (>15%)' },
  { value: 17, color: '#00897b', label: '4.6 Tree cover needleleaved evergreen closed (>40%)' },
  { value: 18, color: '#80cbc4', label: '4.7 Tree cover needleleaved deciduous closed to open (>15%)' },
  { value: 19, color: '#00796b', label: '4.8 Tree cover needleleaved deciduous closed (>40%)' },
  { value: 20, color: '#8bc34a', label: '4.9 Tree cover mixed leaf type open (broadleaved and needleleaved)' },
  { value: 21, color: '#558b2f', label: '4.10 Tree cover mixed leaf type closed (broadleaved and needleleaved)' },
  { value: 22, color: '#26a69a', label: '4.11 Flooded tree cover open' },
  { value: 23, color: '#009688', label: '4.12 Flooded tree cover closed' },
  { value: 24, color: '#00695c', label: '4.13 Other flooded tree cover' },
  { value: 25, color: '#9e9d24', label: '4.14 Other tree cover' },
  { value: 26, color: '#e67e22', label: '5.1 Shrubland and heathland' },
  { value: 27, color: '#e0cda9', label: '6.1 True desert' },
  { value: 28, color: '#c0a46b', label: '6.2 Semi-arid' },
  { value: 29, color: '#ac5f3a', label: '6.3 Semi-arid shrubland' },
  { value: 30, color: '#d7ccc8', label: '6.4 Rocks and mountains peaks' },
  { value: 31, color: '#f0f0f0', label: '6.5 Snow and ice' },
  { value: 32, color: '#1ed9b4', label: '7.1 Wetland - Sparse vegetation' },
  { value: 33, color: '#1abc9c', label: '7.2 Wetland - Grassland' },
  { value: 34, color: '#00897b', label: '7.3 Wetland - Shrubland' },
  { value: 35, color: '#4169e1', label: '8.1 Water - Permanent' },
  { value: 36, color: '#4189e1', label: '8.2 Water - Seasonal' },
  { value: 37, color: '#6c5ce7', label: '9.1 Mangrove' },
  { value: 38, color: '#9a8cff', label: '9.2 Coastal wetlands' },
  { value: 39, color: '#f5deb3', label: '9.3 Beaches and dunes' },
  { value: 40, color: '#add8e6', label: '10.1 Aquaculture' },
]
const GHLC_LEVEL1_30M = { 1: '#d73027', 2: '#ffd700', 3: '#ffd700', 4: '#ffd700', 5: '#ffd700', 6: '#ffd700', 7: '#ffd700', 8: '#ffd700', 9: '#ffd700', 10: '#e49f2e', 11: '#e49f2e', 12: '#006e00', 13: '#006e00', 14: '#006e00', 15: '#006e00', 16: '#006e00', 17: '#006e00', 18: '#006e00', 19: '#006e00', 20: '#006e00', 21: '#006e00', 22: '#006e00', 23: '#006e00', 24: '#006e00', 25: '#006e00', 26: '#e67e22', 27: '#c0a46b', 28: '#c0a46b', 29: '#c0a46b', 30: '#c0a46b', 31: '#c0a46b', 32: '#1abc9c', 33: '#1abc9c', 34: '#1abc9c', 35: '#4169e1', 36: '#4169e1', 37: '#6c5ce7', 38: '#6c5ce7', 39: '#6c5ce7', 40: '#add8e6' }
const GHLC_LEVEL1_LEGEND_30M = [
  { color: '#d73027', label: '1. Built-up' },
  { color: '#ffd700', label: '2. Cropland (Annual & Perennial)' },
  { color: '#e49f2e', label: '3. Grassland' },
  { color: '#006e00', label: '4. Forest, forest plantations and woodland' },
  { color: '#e67e22', label: '5. Shrubland and heathland' },
  { color: '#c0a46b', label: '6. Sparsely vegetated ecosystems' },
  { color: '#1abc9c', label: '7. Inland wetlands' },
  { color: '#4169e1', label: '8. Water bodies' },
  { color: '#6c5ce7', label: '9. Coastal ecosystems' },
  { color: '#add8e6', label: '10. Aquaculture' },
]

// 10 m (STAC land.cover_ifad.mcdt_10m_go): one map, 2020 reference year.
export const GHLC_10M_CLASSES = [
  { value: 1, color: '#d73027', label: '1.1 Built-up' },
  { value: 2, color: '#614d1a', label: '2.1 Annual crops - soybean' },
  { value: 3, color: '#f9a41a', label: '2.2 Annual crops - maize' },
  { value: 4, color: '#f9e79f', label: '2.3 Annual crops - rice' },
  { value: 5, color: '#ffd700', label: '2.4 Annual crops - other' },
  { value: 6, color: '#9e7c2d', label: '2.5 Perenial crops - coffee' },
  { value: 7, color: '#6e4b3a', label: '2.6 Perenial crops - cocoa' },
  { value: 8, color: '#a1887f', label: '2.7 Perenial crops - rubber' },
  { value: 9, color: '#d4ac0d', label: '2.8 Perenial crops - palm' },
  { value: 10, color: '#81c784', label: '3.1 Cultivated grassland' },
  { value: 11, color: '#a5d86a', label: '3.2 Natural grassland' },
  { value: 12, color: '#526140', label: '3.3 Other grassland' },
  { value: 13, color: '#66d16a', label: '4.1 Tree cover broadleaved evergreen open' },
  { value: 14, color: '#1b9e77', label: '4.2 Tree cover broadleaved evergreen closed' },
  { value: 15, color: '#388e3c', label: '4.3 Tree cover broadleaved deciduous closed to open (>15%)' },
  { value: 16, color: '#006e00', label: '4.4 Tree cover broadleaved deciduous closed (>40%)' },
  { value: 17, color: '#4db6ac', label: '4.5 Tree cover needleleaved evergreen closed to open (>15%)' },
  { value: 18, color: '#00897b', label: '4.6 Tree cover needleleaved evergreen closed (>40%)' },
  { value: 19, color: '#80cbc4', label: '4.7 Tree cover needleleaved deciduous closed to open (>15%)' },
  { value: 20, color: '#00796b', label: '4.8 Tree cover needleleaved deciduous closed (>40%)' },
  { value: 21, color: '#8bc34a', label: '4.9 Tree cover mixed leaf type open (broadleaved and needleleaved)' },
  { value: 22, color: '#558b2f', label: '4.10 Tree cover mixed leaf type closed (broadleaved and needleleaved)' },
  { value: 23, color: '#26a69a', label: '4.11 Flooded tree cover open' },
  { value: 24, color: '#009688', label: '4.12 Flooded tree cover closed' },
  { value: 25, color: '#00695c', label: '4.13 Other flooded tree cover' },
  { value: 26, color: '#9e9d24', label: '4.14 Other tree cover' },
  { value: 27, color: '#e67e22', label: '5.1 Shrubland and heathland' },
  { value: 28, color: '#c98804', label: '5.2 Other shrubland' },
  { value: 29, color: '#e0cda9', label: '6.1 True desert' },
  { value: 30, color: '#c0a46b', label: '6.2 Semi-arid' },
  { value: 31, color: '#ac5f3a', label: '6.3 Semi-arid shrubland' },
  { value: 32, color: '#f0f0f0', label: '6.5 Snow and ice' },
  { value: 33, color: '#d7ccc8', label: '6.4 Rocks and mountains peaks' },
  { value: 34, color: '#c9b1c9', label: '6.6 Moss and lichen' },
  { value: 35, color: '#1ed9b4', label: '7.1 Wetland - Sparse vegetation' },
  { value: 36, color: '#1abc9c', label: '7.2 Wetland - Grassland' },
  { value: 37, color: '#00897b', label: '7.3 Wetland - Shrubland' },
  { value: 38, color: '#2c5747', label: '7.4 Wetland - other wetlands' },
  { value: 39, color: '#4169e1', label: '8.1 Water - Permanent' },
  { value: 40, color: '#4189e1', label: '8.2 Water - Seasonal' },
  { value: 41, color: '#6c5ce7', label: '9.1 Mangrove' },
  { value: 42, color: '#9a8cff', label: '9.2 Coastal wetlands' },
  { value: 43, color: '#f5deb3', label: '9.3 Beaches and dunes' },
  { value: 44, color: '#f5deb3', label: '10.1 Aquaculture' },
]
const GHLC_LEVEL1_10M = { 1: '#d73027', 2: '#ffd700', 3: '#ffd700', 4: '#ffd700', 5: '#ffd700', 6: '#ffd700', 7: '#ffd700', 8: '#ffd700', 9: '#ffd700', 10: '#e49f2e', 11: '#e49f2e', 12: '#e49f2e', 13: '#228b22', 14: '#228b22', 15: '#228b22', 16: '#228b22', 18: '#228b22', 19: '#228b22', 20: '#228b22', 21: '#228b22', 22: '#228b22', 23: '#228b22', 24: '#228b22', 25: '#228b22', 26: '#228b22', 17: '#228b22', 27: '#e67e22', 28: '#e67e22', 29: '#c0a46b', 30: '#c0a46b', 31: '#c0a46b', 32: '#c0a46b', 33: '#c0a46b', 34: '#c0a46b', 35: '#1abc9c', 36: '#1abc9c', 37: '#1abc9c', 38: '#1abc9c', 39: '#4169e1', 40: '#4169e1', 41: '#6c5ce7', 42: '#6c5ce7', 43: '#6c5ce7', 44: '#add8e6' }
const GHLC_LEVEL1_LEGEND_10M = [
  { color: '#d73027', label: '1. Built-up' },
  { color: '#ffd700', label: '2. Cropland (Annual & Perennial)' },
  { color: '#e49f2e', label: '3. Grassland' },
  { color: '#228b22', label: '4. Forest, forest plantations and woodland' },
  { color: '#e67e22', label: '5. Shrubland and heathland' },
  { color: '#c0a46b', label: '6. Sparsely vegetated ecosystems' },
  { color: '#1abc9c', label: '7. Inland wetlands' },
  { color: '#4169e1', label: '8. Water bodies' },
  { color: '#6c5ce7', label: '9. Coastal ecosystems' },
  { color: '#add8e6', label: '10. Aquaculture' },
]

const COG = (resolution, year) =>
  `https://ceph.opengeohub.org/arco/lclu/ghlc_lcvmcdt_c_${resolution}_sg_${year}0101_${year}1231_go01_epsg4326_v1.2.0_srl04.tif`

export const GHLC_PRODUCTS = {
  '30m': {
    years: [2020, 2021, 2022, 2023, 2024],
    defaultYear: 2024,
    maxNativeZoom: 14,
    cog: year => COG('30m', year),
    colormaps: { Default: GHLC_LEVEL1_30M, Detailed: Object.fromEntries(GHLC_CLASSES.map(c => [c.value, c.color])) },
    legends: { Default: GHLC_LEVEL1_LEGEND_30M, Detailed: GHLC_CLASSES.map(({ color, label }) => ({ color, label })) },
  },
  '10m': {
    years: [2020],
    defaultYear: 2020,
    maxNativeZoom: 16,
    cog: year => COG('10m', year),
    colormaps: { Default: GHLC_LEVEL1_10M, Detailed: Object.fromEntries(GHLC_10M_CLASSES.map(c => [c.value, c.color])) },
    legends: { Default: GHLC_LEVEL1_LEGEND_10M, Detailed: GHLC_10M_CLASSES.map(({ color, label }) => ({ color, label })) },
  },
}

export const ghlcLegend = (product, mode) =>
  GHLC_PRODUCTS[product].legends[GHLC_MODES.includes(mode) ? mode : GHLC_DEFAULT_MODE]

export const ghlcTileUrl = (product, year, mode) => {
  const config = GHLC_PRODUCTS[product]
  const colormap = config.colormaps[GHLC_MODES.includes(mode) ? mode : GHLC_DEFAULT_MODE]
  const chosenYear = config.years.includes(year) ? year : config.defaultYear
  // Encoded so the JSON braces never reach Leaflet's {z}/{x}/{y} template.
  const query = `url=${encodeURIComponent(config.cog(chosenYear))}&colormap=${encodeURIComponent(JSON.stringify(colormap))}`
  return `https://titiler.opengeohub.org/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?${query}`
}

function GhlcLandCoverLayer({ product = '30m', year, mode = GHLC_DEFAULT_MODE, opacity = 0.7 }) {
  const url = useMemo(() => ghlcTileUrl(product, year, mode), [product, year, mode])
  return (
    <TileLayer
      key={url}
      url={url}
      opacity={opacity}
      maxNativeZoom={GHLC_PRODUCTS[product].maxNativeZoom}
      attribution={GHLC_ATTRIBUTION}
      zIndex={3}
    />
  )
}

export default GhlcLandCoverLayer
